import React from 'react';
import { ArrowRight, BarChart3, LayoutDashboard, ShoppingCart, Smartphone, Layers, Cpu, ShieldCheck, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const whatsappNumber = "918796118101";
  const whatsappMessage = encodeURIComponent("Hello! I would like to talk to an expert about a web/mobile development project.");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const appCards = [
    { label: 'Enterprise Web Applications', icon: LayoutDashboard, className: 'hero-app-card-primary' },
    { label: 'E-Commerce', icon: ShoppingCart, className: 'hero-app-card-secondary' },
    { label: 'Websites', icon: Smartphone, className: 'hero-app-card-tertiary' },
    { label: 'UX/UI Design', icon: BarChart3, className: 'hero-app-card-quaternary' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gray-950">
      {/* Background Gradients & Mesh */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-slate-950 to-gray-900" />
        <div className="hero-soft-mesh absolute inset-0 opacity-40" />
        <div className="hero-circuit-grid absolute inset-0 opacity-20" />
        <div className="hero-data-streams absolute inset-0 opacity-25" />
        {/* Glow Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />
      </div>

      {/* Main Container */}
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid min-h-screen items-center gap-12 pt-28 pb-16 lg:grid-cols-[0.9fr_1.1fr] lg:pt-20">

          {/* Left Text Content */}
          <div className="max-w-2xl text-left hero-readable-content">
            <div className="inline-flex items-center space-x-2  px-4 py-2 mb-6 ">
              <span className="text-cyan-400 text-sm md:text-sm font-semibold tracking-wide uppercase">
                Web & Mobile Development Company
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight text-white tracking-tight">
              Building Digital Solutions <br />
              <span className="bg-gradient-to-r from-orange-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                That Drive Growth
              </span>
            </h1>

            <p className="text-base md:text-lg text-gray-300 mb-8 leading-relaxed max-w-xl">
              We build high-performance websites, eCommerce platforms, healthcare solutions, and enterprise web applications that help businesses innovate, scale, and grow in the digital age.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 mb-10">
              <a href="#services">
                <Button className="group bg-gradient-to-r from-orange-500 via-amber-500 to-cyan-500 hover:from-orange-600 hover:to-cyan-600 text-white px-8 py-6 text-base font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 hover:scale-105">
                  Explore Services
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>

              {/* Talk To Experts - Direct WhatsApp Button */}
              <a href={whatsappUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-2 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 px-8 py-6 text-base font-semibold rounded-xl backdrop-blur-sm transition-all duration-300 flex items-center gap-2.5 shadow-lg shadow-emerald-500/10 hover:shadow-emerald-500/30">
                  <MessageCircle className="w-5 h-5 text-emerald-400" />
                  <span>Talk To Experts (+91 8796 118 101)</span>
                </Button>
              </a>
            </div>

            {/* Quick Trust Badges */}
            <div className="pt-6 border-t border-gray-800/80 flex flex-wrap items-center gap-6 text-base text-gray-400">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-cyan-400" />
                <span>Enterprise Security</span>
              </div>
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-orange-400" />
                <span>AI-Powered Tech</span>
              </div>
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-blue-400" />
                <span>Custom Architecture</span>
              </div>
            </div>
          </div>

          {/* Right Product Screen & Mockup Cards */}
          <div className="relative hidden min-h-[540px] lg:block" aria-hidden="true">
            <div className="hero-app-gradient absolute inset-y-8 right-0 w-full rounded-[40px] border border-cyan-500/20 bg-gray-900/50 backdrop-blur-md" />
            <div className="hero-app-orbit absolute inset-0">
              <div className="hero-product-screen shadow-2xl shadow-cyan-500/10 border border-gray-700/60 rounded-2xl bg-slate-900/90 overflow-hidden">
                <div className="hero-product-topbar bg-gray-800/80 px-4 py-3 border-b border-gray-700/50 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500/80" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <span className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <div className="p-6 grid grid-cols-3 gap-4">
                  <div className="col-span-2 h-32 rounded-xl bg-gradient-to-br from-cyan-900/30 to-blue-900/20 border border-cyan-500/20 p-4">
                    <div className="text-2xl font-bold text-white pt-6">Custom Web Applications</div>
                    <div className="text-[11px] text-gray-400 mt-2">Secure • Scalable • Future-Ready</div>
                  </div>
                  <div className="col-span-1 h-32 py-6 rounded-xl bg-gray-800/50 border border-gray-700 p-4">
                    <div className="text-sm font-medium text-orange-400 mb-1">Fast Development</div>
                    <div className="text-2xl font-bold text-white">Digital Solutions</div>

                  </div>
                  <div className="col-span-3 h-40 rounded-xl bg-gray-800/40 border border-gray-700/60 p-4 flex flex-col justify-between">
                    <div className="flex items-center justify-between text-sm text-gray-300">
                      <span>Fast, responsive & SEO-ready</span>
                      <span className="text-green-400 font-mono">Built to automate your business</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="px-2 py-1 text-[11px] rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">React / Next.js</span>
                      <span className="px-2 py-1 text-[11px] rounded bg-orange-500/10 text-orange-300 border border-orange-500/20">Node / APIs</span>
                      <span className="px-2 py-1 text-[11px] rounded bg-purple-500/10 text-purple-300 border border-purple-500/20">AI / LLM</span>
                      <span className="px-2 py-1 text-[11px] rounded bg-blue-500/10 text-blue-300 border border-blue-500/20">AWS Cloud</span>
                    </div>
                  </div>
                </div>
              </div>

              {appCards.map(({ label, icon: Icon, className }) => (
                <div key={label} className={`hero-app-card ${className} backdrop-blur-md bg-gray-800/80 border border-cyan-500/30 text-white shadow-lg`}>
                  <Icon className="h-5 w-5 text-cyan-400" />
                  <span className="text-sm font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
